import {
  AdminInput,
  AdminSpinner,
  Button,
  Disclaimer,
  Div,
  DropdownMenuComponent,
  H5,
  Icon,
  Img,
  LocationPinIcon,
  Overlay,
  P,
  Radio,
  getAllVerifiedLocations,
  isAdmin,
  lightTheme,
  overlayAnimationVariant,
  restaurantImage,
  useDisableBodyScroll,
  Box,
  handleSetUrlSearchParams,
  reportError,
} from '@vgrubs/components';
import {
  SimpleLocationEntity,
  SimpleLocationWithTodayDataResponse,
} from 'components/src/types/location.types';
import { getLocationsByIds } from 'components/src/api/locations.api';
import 'flatpickr/dist/flatpickr.css';
import { AnimatePresence } from 'framer-motion';
import debouce from 'lodash.debounce';
import {
  ChangeEventHandler,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams, SetURLSearchParams } from 'react-router-dom';
import {
  LocationsPicker,
  ScrollableWrapper,
} from './AdminLocationsPickerStyled';
import { getPartnerLocations } from 'components/src/api/partner/locations.partner-api';

export const AdminLocationsPicker = ({
  selectedLocations,
  setSelectedLocations,
  handleSubmit,
  toTop,
  persistToUrl = false,
  onLocationsLoaded,
  setSearchParams,
}: {
  selectedLocations: SimpleLocationEntity[];
  setSelectedLocations: Dispatch<SetStateAction<SimpleLocationEntity[]>>;
  handleSubmit: (
    locationIds?: number[],
    locationObjects?: SimpleLocationEntity[]
  ) => void;
  toTop?: boolean;
  persistToUrl?: boolean;
  onLocationsLoaded?: (locations: SimpleLocationEntity[]) => void;
  setSearchParams?: SetURLSearchParams;
}) => {
  const [searchParams, internalSetSearchParams] = useSearchParams();
  const finalSetSearchParams = setSearchParams || internalSetSearchParams;
  const [loadingInitialData, setLoadingInitialData] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const selectedLocationsRef = useRef(selectedLocations);
  // Keyboard navigation state — roving-tabindex pointer into the list.
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Keep ref in sync with prop
  useEffect(() => {
    selectedLocationsRef.current = selectedLocations;
  }, [selectedLocations]);
  const [data, setData] = useState<
    (SimpleLocationEntity | SimpleLocationWithTodayDataResponse)[]
  >([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const [ref, inView] = useInView();
  const [hasMore, setHasMore] = useState(true);

  useDisableBodyScroll(dropdown);

  async function getLocations(
    search: boolean,
    page: number,
    hasMore: boolean,
    query: string
  ) {
    if (!hasMore) return;
    try {
      const params = isAdmin
        ? {
            query,
            page,
            limit: 10,
          }
        : {
            query,
            page,
            limit: 10,
            verified: true,
          };

      const getFunction = isAdmin
        ? getAllVerifiedLocations
        : getPartnerLocations;

      setLoading(true);
      const { data } = await getFunction(params);

      if (!search) {
        if (data.data[0]) {
          setHasMore(true);
          setData((prevItems) => [...prevItems, ...data.data]);
        } else {
          setHasMore(false);
        }
      } else {
        if (data.data[0]) {
          setHasMore(true);
          setData(() => [...data.data]);
        } else {
          setData(() => [...data.data]);
          setHasMore(false);
        }
      }

      setPage((prevPage) => prevPage + 1);
    } catch (error) {
      // ErrorToast(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  // Load locations from URL on mount
  useEffect(() => {
    const locationIdsParam = searchParams.get('locationIds');
    const locationIds = locationIdsParam
      ? locationIdsParam
          .split(',')
          .map((id) => parseInt(id.trim()))
          .filter((id) => !isNaN(id))
      : [];

    if (locationIds.length > 0 && selectedLocations.length === 0) {
      async function loadLocationsFromURL() {
        setLoadingInitialData(true);
        try {
          const { data: locationData } = await getLocationsByIds(locationIds);

          setSelectedLocations(locationData.data);

          // Notify parent component about loaded locations
          if (onLocationsLoaded) {
            onLocationsLoaded(locationData.data);
          }
        } catch (error) {
          reportError(error);
        } finally {
          setLoadingInitialData(false);
        }
      }

      loadLocationsFromURL();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load dropdown list when dropdown is opened
  useEffect(() => {
    if (dropdown && data.length === 0) {
      setPage(1);
      setHasMore(true);
      getLocations(false, 1, true, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdown]);

  useEffect(() => {
    if (inView && data.length > 0) {
      getLocations(false, page, hasMore, query);
    }
  }, [inView]);

  // When the dropdown opens, focus the search input so the user can type
  // immediately. Reset the keyboard cursor on close.
  useEffect(() => {
    if (!dropdown) {
      setFocusedIndex(-1);
      return;
    }
    const raf = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [dropdown]);

  // Seed the roving-tabindex cursor to the first item once data is loaded.
  // Only ONE item in the list has tabIndex=0 at a time — so Tab from a
  // focused item falls through naturally to the Reset button below
  // (instead of walking through every location). Arrow keys move the
  // cursor between items.
  useEffect(() => {
    if (dropdown && data.length > 0 && focusedIndex === -1) {
      setFocusedIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdown, data.length]);

  // When the focused index changes (arrow keys) OR when data finishes
  // loading after the user already pressed ArrowDown, move DOM focus to
  // the targeted item. Uses rAF so the item ref is mounted before .focus().
  useEffect(() => {
    if (focusedIndex < 0) return;
    if (focusedIndex >= data.length) return;
    const raf = requestAnimationFrame(() => {
      itemRefs.current[focusedIndex]?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [focusedIndex, data.length]);

  const handleListItemKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    location: SimpleLocationEntity,
    index: number,
    count: number
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(Math.min(index + 1, count - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      // ArrowUp on the first item returns focus to the search input.
      if (index === 0) {
        setFocusedIndex(-1);
        searchInputRef.current?.focus();
      } else {
        setFocusedIndex(index - 1);
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      setFocusedIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setFocusedIndex(count - 1);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleSelectLocation(location);
    }
  };

  // ArrowDown on the search input enters the list. We always set the
  // index — even if data is still loading — so when items finally render,
  // the focus effect (which also depends on `data.length`) catches up.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(0);
    }
  };

  const handleSelectLocation = (newItem: SimpleLocationEntity) => {
    const index = selectedLocations.findIndex((item) => item.id === newItem.id);
    if (index === -1) {
      // Item doesn't exist, so add it
      setSelectedLocations((prevItems) => {
        const newLocations = [...prevItems, newItem];
        selectedLocationsRef.current = newLocations;
        return newLocations;
      });
    } else {
      // Item exists, so remove it
      setSelectedLocations((prevItems) => {
        const newLocations = prevItems.filter((item) => item.id !== newItem.id);
        selectedLocationsRef.current = newLocations;
        return newLocations;
      });
    }
  };

  function isLocationSelected(item: SimpleLocationEntity) {
    return selectedLocations.some((location) => location.id === item.id);
  }

  function renderLocationInputPlaceholder() {
    if (selectedLocations.length === 0) {
      return 'All Locations';
    } else if (selectedLocations.length === 1) {
      return selectedLocations[0]?.name;
    } else return `${selectedLocations.length} Locations`;
  }

  const handleChange: ChangeEventHandler<HTMLInputElement> = async (e) => {
    setHasMore(true);
    setPage(1);
    setQuery(e.target.value);
    getLocations(true, 1, true, e.target.value);
  };

  const debouncedResults = useMemo(() => {
    return debouce(handleChange, 500);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  return (
    <AnimatePresence>
      <Div $relative $height="40px" $heightSM="40px">
        <LocationsPicker
          $opened={dropdown}
          onClick={() => setDropdown((prev) => !prev)}
          aria-haspopup="dialog"
          aria-expanded={dropdown}
          aria-label="Filter by location"
        >
          <Div>{renderLocationInputPlaceholder()}</Div>

          <Icon $opacity="0.5">
            <LocationPinIcon />
          </Icon>
        </LocationsPicker>

        <DropdownMenuComponent
          isOpen={dropdown}
          maxHeight="70vh"
          padding="0"
          fullWidth
          toTop={toTop}
        >
          {(loading || loadingInitialData) && <AdminSpinner absolute small />}

          <Div $p="20px">
            <AdminInput
              ref={searchInputRef}
              search
              onChange={debouncedResults}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search Location"
            />
          </Div>
          <ScrollableWrapper
            role="listbox"
            aria-multiselectable="true"
            aria-label="Locations"
            // overflow:auto regions are auto-tabbable in Chrome, which steals
            // focus from the actual listbox options. Opt the wrapper out;
            // keyboard nav happens via ArrowDown from the search input.
            tabIndex={-1}
          >
            {data.length === 0 && !hasMore && (
              <Div
                $flex
                $center
                $column
                $height="calc(70vh - 300px)"
                $heightSM="calc(60vh - 300px)"
              >
                <Img
                  src={restaurantImage}
                  alt="restaurant"
                  $width="250px"
                  $widthSM="150px"
                />
                <H5>No Locations</H5>
              </Div>
            )}
            {data.map((lc, i) => {
              const l = (
                isAdmin
                  ? lc
                  : (lc as SimpleLocationWithTodayDataResponse).location
              ) as SimpleLocationEntity;
              return (
                <Box
                  key={i}
                  ref={(el: HTMLDivElement | null) => {
                    itemRefs.current[i] = el;
                  }}
                  role="option"
                  aria-selected={isLocationSelected(l)}
                  // Roving tabindex: only the currently-focused item is
                  // in Tab order. Tab from it falls through to Reset
                  // (skipping the rest of the list). Arrow keys move
                  // the cursor between items.
                  tabIndex={focusedIndex === i ? 0 : -1}
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
                    handleListItemKeyDown(e, l, i, data.length)
                  }
                  $mb="8"
                  $flex
                  $alignCenter
                  $between
                  onClick={() => handleSelectLocation(l)}
                  $gap="16px"
                  $pointer
                >
                  <div>
                    <P
                      $left
                      $bold
                      $mb="8"
                      $color={isLocationSelected(l) && lightTheme.primary}
                      $lineHeight="1.2"
                    >
                      {l.name}
                    </P>
                    <Disclaimer $left $mb="0">
                      {l.address}
                    </Disclaimer>
                  </div>

                  <Radio checked={isLocationSelected(l)} />
                </Box>
              );
            })}
            <div ref={ref}></div>
          </ScrollableWrapper>

          <Div $p="20px">
            <Button
              $selected
              $gray
              $w="100%"
              $wSM="100%"
              onClick={() => setSelectedLocations([])}
              $mb="16"
            >
              Reset Selection
            </Button>
            <Button
              $selected
              $w="100%"
              $wSM="100%"
              onClick={() => {
                // Persist to URL if enabled - use ref to get latest value
                const currentLocations = selectedLocationsRef.current;
                if (persistToUrl) {
                  const locationIds =
                    currentLocations.length > 0
                      ? currentLocations
                          .map((loc) => loc.id.toString())
                          .join(',')
                      : null;
                  handleSetUrlSearchParams(
                    'locationIds',
                    locationIds,
                    finalSetSearchParams
                  );
                }
                setDropdown((prev) => !prev);
                // Pass location IDs and objects from ref to ensure we have the latest values
                // even if parent state hasn't updated yet
                handleSubmit(
                  currentLocations.map((loc) => loc.id),
                  currentLocations
                );
              }}
            >
              Confirm
            </Button>
          </Div>
        </DropdownMenuComponent>
      </Div>

      {dropdown && (
        <Overlay
          key="modalOverlay"
          variants={overlayAnimationVariant}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={() => setDropdown(false)}
        />
      )}
    </AnimatePresence>
  );
};
